import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram, Mail, MapPin, ArrowUp, MessageCircle } from "lucide-react";
import sercomLogo from "@/assets/sercom-logo.png";
import { trackWhatsAppClick, trackCallClick, trackEmailClick } from "@/lib/analytics";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import ContactPopover from "@/components/ContactPopover";

const WHATSAPP_MSG = "Hola, os escribo a través de la web de Sercom. Estoy interesado/a en una de vuestras propiedades y me gustaría recibir más información o concertar una visita. ¡Gracias!";
const WHATSAPP_URL = `https://wa.me/34644100551?text=${encodeURIComponent(WHATSAPP_MSG)}`;
const WHATSAPP_PHONE = "+34 644 100 551";
const EMAIL = "sercom.construction@gmail.com";
const EMAIL_SUBJECT = "Interés por una casa";
const EMAIL_BODY = "Hola,\n\nHe visto una de vuestras casas en la web de Sercom y me gustaría recibir más información o, si fuera posible, concertar una visita.\n\nQuedo a la espera de vuestra respuesta.\n\nUn saludo.";
const EMAIL_HREF = `mailto:${EMAIL}?subject=${encodeURIComponent(EMAIL_SUBJECT)}&body=${encodeURIComponent(EMAIL_BODY)}`;

const navLinks = [
  { to: "/", label: "Inicio" },
  { to: "/Propiedades", label: "Propiedades" },
  { to: "/Alquileres", label: "Alquileres" },
  { to: "/Servicios", label: "Servicios" },
];

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const lastY = useRef(0);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
      // Ocultar al bajar, mostrar al subir. No ocultar cerca del top.
      if (y > 120 && y > lastY.current) {
        setHidden(true);
      } else if (y < lastY.current) {
        setHidden(false);
      }
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-surface shadow-lg" : "bg-background/95 backdrop-blur-md"
      } ${hidden && !mobileOpen ? "-translate-y-full" : "translate-y-0"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-24 md:h-28">
        <Link to="/" className="flex items-center gap-3">
          <img src={sercomLogo} alt="Sercom - Viviendas de obra nueva en el Vallès Occidental" className="h-24 md:h-28 w-auto object-contain" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`relative text-sm font-medium tracking-wide transition-colors hover:text-accent after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-accent after:transition-transform after:duration-300 ${
                location.pathname === link.to ? "after:scale-x-100" : "after:scale-x-0 hover:after:scale-x-100"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/Contacto"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl"
          >
            Contacto
          </Link>
        </nav>

        {/* Mobile hamburger */}
        <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"} aria-expanded={mobileOpen}>
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background border-t overflow-hidden"
          >
            <nav className="flex flex-col px-6 py-4 gap-4">
              {navLinks.map((link) => (
                <Link key={link.to} to={link.to} className="text-lg font-medium py-2">
                  {link.label}
                </Link>
              ))}
              <Link
                to="/Contacto"
                onClick={() => setMobileOpen(false)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 font-medium text-primary-foreground shadow-lg"
              >
                Contacto
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-sercom-black text-white/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          <div>
            <img src={sercomLogo} alt="Sercom Construction & Services - Inmobiliaria en el Vallès Occidental" className="h-20 md:h-24 w-auto object-contain mb-4 -ml-2 brightness-0 invert" />
            <p className="text-sm leading-relaxed text-white/80">
              Construimos y vendemos viviendas de obra nueva en el Vallès Occidental y provincia de Barcelona, con acabados de calidad.
            </p>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm tracking-wider uppercase">Contacto</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2">
                <WhatsAppIcon className="h-6 w-6" />
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick()} className="hover:text-white transition-colors">
                  {WHATSAPP_PHONE}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-accent" />
                <a href={EMAIL_HREF} onClick={() => trackEmailClick()} className="hover:text-white transition-colors">{EMAIL}</a>
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-accent" />
                <span>Vallès Occidental, Barcelona</span>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm tracking-wider uppercase">Síguenos</h3>
            <a
              href="https://www.instagram.com/sercom.cs/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 hover:text-white transition-colors"
            >
              <Instagram className="w-5 h-5" />
              @sercom.cs
            </a>
          </div>
        </div>
        <div className="border-t border-white/10 mt-12 pt-8 text-center text-xs text-white/85">
          © {new Date().getFullYear()} Sercom Construction & Services. Todos los derechos reservados.
        </div>
      </div>
    </footer>
  );
}

function FloatingButtons() {
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 items-center">
      <AnimatePresence>
        {showTop && (
          <motion.button
            key="top"
            initial={{ opacity: 0, scale: 0.6, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.6, y: 20 }}
            transition={{ duration: 0.2 }}
            onClick={() => {
              const start = window.scrollY;
              const duration = 900;
              const startTime = performance.now();
              const ease = (t: number) => 1 - Math.pow(1 - t, 3);
              const step = (now: number) => {
                const t = Math.min(1, (now - startTime) / duration);
                window.scrollTo(0, start * (1 - ease(t)));
                if (t < 1) requestAnimationFrame(step);
              };
              requestAnimationFrame(step);
            }}
            className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg bg-sercom-black text-white border border-accent/40 hover:scale-110 hover:border-accent transition-transform"
            aria-label="Volver arriba"
          >
            <ArrowUp className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>
      <ContactPopover
        align="end"
        side="top"
        showFormLink
        trigger={
          <button
            aria-label="Opciones de contacto"
            className="group flex h-14 w-14 items-center justify-center rounded-full bg-sercom-black text-white shadow-lg ring-1 ring-accent/40 transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:ring-accent"
          >
            <MessageCircle className="h-6 w-6 transition-transform duration-300 group-hover:rotate-6" />
          </button>
        }
      />
    </div>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <FloatingButtons />
    </div>
  );
}
